// btw-front/src/components/deal/AdoptionFilePicker.jsx
// 도입 증빙 파일 다중 선택 — 업로드 전 File[] 을 보유하는 피커. 실제 전송은 부모가 수행.
// 확장자·크기·개수 상한은 서버가 딜 단건 응답(adoptionFileLimits)으로 내려주는 값을 사용해
// 서버 검증(fileService.dealAdoptionFileUpload)과 단일 기준 유지. 응답 누락 시 기본값으로 폴백.
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";

const DEFAULT_LIMITS = {
  maxFiles: 10,
  maxSizeBytes: 10 * 1024 * 1024,
  allowedExtensions: [
    ".pdf",
    ".doc",
    ".docx",
    ".ppt",
    ".pptx",
    ".xls",
    ".xlsx",
    ".hwp",
    ".hwpx",
    ".jpg",
    ".jpeg",
    ".png",
    ".zip",
  ],
};

// 서버 응답 → 검증용 limits 정규화(누락 필드는 기본값)
export function normalizeAdoptionLimits(limits) {
  const l = limits || {};
  return {
    maxFiles: Number(l.maxFiles) > 0 ? Number(l.maxFiles) : DEFAULT_LIMITS.maxFiles,
    maxSizeBytes: Number(l.maxSizeBytes) > 0 ? Number(l.maxSizeBytes) : DEFAULT_LIMITS.maxSizeBytes,
    allowedExtensions:
      Array.isArray(l.allowedExtensions) && l.allowedExtensions.length
        ? l.allowedExtensions.map((e) => String(e).toLowerCase())
        : DEFAULT_LIMITS.allowedExtensions,
  };
}

// <input accept> 문자열
export function adoptionAccept(limits) {
  return normalizeAdoptionLimits(limits).allowedExtensions.join(",");
}

export function formatFileSize(bytes) {
  if (bytes == null) return "";
  const mb = 1024 * 1024;
  if (bytes >= mb) return `${(bytes / mb).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

function extOf(name) {
  const i = (name || "").lastIndexOf(".");
  return i >= 0 ? name.slice(i).toLowerCase() : "";
}

/**
 * 선택 파일 검증 — 부모가 업로드 직전에도 호출 가능.
 * 반환: { ok: File[], error: string | null } — 첫 오류에서 중단(간결한 안내).
 */
export function validateAdoptionFiles(candidates, existingCount, t, limits) {
  const { maxFiles, maxSizeBytes, allowedExtensions } = normalizeAdoptionLimits(limits);
  const allowed = new Set(allowedExtensions);
  const maxSizeMb = Math.round(maxSizeBytes / (1024 * 1024));
  const list = Array.from(candidates || []);
  if (existingCount + list.length > maxFiles) {
    return { ok: [], error: t("partners.deals.detail.adoptionTooMany", { max: maxFiles }) };
  }
  for (const f of list) {
    if (!allowed.has(extOf(f.name))) {
      return { ok: [], error: t("partners.deals.detail.adoptionBadType", { name: f.name }) };
    }
    if (f.size > maxSizeBytes) {
      return {
        ok: [],
        error: t("partners.deals.detail.adoptionTooLarge", { name: f.name, maxSizeMb }),
      };
    }
  }
  return { ok: list, error: null };
}

/**
 * props
 *  - files: File[] (선택된 파일)
 *  - onChange(files: File[])
 *  - onError(message)
 *  - existingCount: 서버에 이미 있는 파일 수(개수 상한 계산용)
 *  - limits: 서버 응답 adoptionFileLimits (없으면 기본값)
 *  - disabled
 */
export default function AdoptionFilePicker({
  files,
  onChange,
  onError,
  existingCount = 0,
  limits,
  disabled = false,
}) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const lim = normalizeAdoptionLimits(limits);

  function handlePick(fileList) {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    // 같은 이름+크기 중복 선택은 무시
    const dedup = picked.filter((p) => !files.some((f) => f.name === p.name && f.size === p.size));
    const { ok, error } = validateAdoptionFiles(dedup, existingCount + files.length, t, lim);
    if (error) {
      onError && onError(error);
      return;
    }
    onChange([...files, ...ok]);
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={disabled}
          onClick={() => inputRef.current && inputRef.current.click()}
          className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:border-accent hover:text-accent disabled:opacity-60"
        >
          {t("partners.deals.detail.adoptionPick")}
        </button>
        <span className="text-xs text-slate-500">
          {t("partners.deals.detail.adoptionPickHint", {
            maxSizeMb: Math.round(lim.maxSizeBytes / (1024 * 1024)),
            maxFiles: lim.maxFiles,
          })}
        </span>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={adoptionAccept(lim)}
          className="hidden"
          onChange={(e) => {
            handlePick(e.target.files);
            e.target.value = "";
          }}
        />
      </div>
      {files.length > 0 && (
        <ul className="mt-3 divide-y divide-slate-100 rounded-lg border border-slate-200">
          {files.map((f, idx) => (
            <li
              key={`${f.name}-${f.size}-${idx}`}
              className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
            >
              <span className="min-w-0 flex-1 truncate text-slate-800">{f.name}</span>
              <span className="shrink-0 text-xs text-slate-500">{formatFileSize(f.size)}</span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange(files.filter((_, i) => i !== idx))}
                className="shrink-0 text-xs text-slate-500 underline-offset-2 hover:text-rose-600 hover:underline disabled:opacity-60"
              >
                {t("partners.deals.detail.adoptionRemovePending")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
